import { Flex } from '@sequencehq/core-components'
import { useAtom } from 'jotai'
import { timelineContainerRefAtom } from '../atoms/timelineUi.atom'
import { timelinePhasesAtom } from '../atoms/timeline.atom'
import reverse from 'lodash/fp/reverse'
import { useMemo } from 'react'
import { useScrollTimeline } from '../hooks/useScrollTimeline'
import { LeftLink, RightLink } from './ScheduleLinks'
import {
  TIMELINE_LINK_FADE_OFFSET,
  TIMELINE_PHASE_ROW_HEIGHT_PX
} from '../timeline.constants'

const usePhaseLinks = () => {
  const [phasesTimeline] = useAtom(timelinePhasesAtom)
  const [timelineContainerRef] = useAtom(timelineContainerRefAtom)
  const timelineScroll = useScrollTimeline()

  const currentTimelineContainerWidth = timelineContainerRef?.clientWidth ?? 0

  const reversedPhases = reverse(phasesTimeline)

  /**
   * Get the next/latest phases which are not shown in the timeline
   * viewport right now.
   */
  const lastPastOffscreenPhase = reversedPhases.find(
    p => p.startDate.offset < timelineScroll.timelineScrollPosition
  )

  const firstFutureOffscreenPhase = phasesTimeline.find(
    p =>
      p.startDate.offset >
      timelineScroll.timelineScrollPosition + currentTimelineContainerWidth
  )

  const showLeftLink = useMemo(() => {
    if (!lastPastOffscreenPhase) {
      return false
    }

    /**
     * As we approach the point of revealing the phase through normal
     * scrolling, we want to hide it to avoid overlapping the link
     * and the underling label.
     */
    if (
      lastPastOffscreenPhase.startDate.offset + 250 >
      timelineScroll.timelineScrollPosition
    ) {
      return false
    }

    const phaseIndex = phasesTimeline.findIndex(
      p => p.id === lastPastOffscreenPhase.id
    )
    const phaseAfter = phasesTimeline[phaseIndex + 1]

    /**
     * As we approach the next phase's posiition, we also want to hide the link, so
     * it doesn't overlap with that one, either.
     */
    if (
      phaseAfter &&
      phaseAfter.startDate.offset < timelineScroll.timelineScrollPosition + 250
    ) {
      return false
    }

    return true
  }, [
    lastPastOffscreenPhase,
    phasesTimeline,
    timelineScroll.timelineScrollPosition
  ])

  const showRightLink = useMemo(() => {
    const currentContainerWidth = timelineContainerRef?.clientWidth ?? 0
    if (!firstFutureOffscreenPhase) {
      return false
    }

    /**
     * As we approach the point of revealing the phase label via scrolling
     * to the right, we want to hide the link to avoid overlapping the label.
     */
    if (
      firstFutureOffscreenPhase.startDate.offset - 100 <
      timelineScroll.timelineScrollPosition + currentContainerWidth
    ) {
      return false
    }

    const phaseIndex = phasesTimeline.findIndex(
      p => p.id === firstFutureOffscreenPhase.id
    )
    const phaseBefore = phasesTimeline[phaseIndex - 1]

    /**
     * We also want to avoid overlapping the previous phase's label as we
     * scroll to the left.
     */
    if (
      phaseBefore &&
      phaseBefore.startDate.offset >
        timelineScroll.timelineScrollPosition + currentContainerWidth - 500
    ) {
      return false
    }

    return true
  }, [
    firstFutureOffscreenPhase,
    phasesTimeline,
    timelineScroll.timelineScrollPosition,
    timelineContainerRef?.clientWidth
  ])

  return {
    left: {
      showLink: showLeftLink,
      onClick: () => {
        if (!showLeftLink || !lastPastOffscreenPhase) {
          return
        }

        timelineScroll.scrollTimeline({
          left:
            lastPastOffscreenPhase?.startDate.offset - TIMELINE_LINK_FADE_OFFSET
        })
      },
      label: lastPastOffscreenPhase?.name ?? ''
    },
    right: {
      showLink: showRightLink,
      onClick: () => {
        if (!showRightLink || !firstFutureOffscreenPhase) {
          return
        }

        timelineScroll.scrollTimeline({
          left:
            firstFutureOffscreenPhase?.startDate.offset -
            TIMELINE_LINK_FADE_OFFSET
        })
      },
      label: firstFutureOffscreenPhase?.name ?? ''
    }
  }
}

export const PhaseLeftLinks = () => {
  const {
    left: { showLink, onClick, label }
  } = usePhaseLinks()

  return (
    <Flex height={`${TIMELINE_PHASE_ROW_HEIGHT_PX}px`}>
      <LeftLink showLink={showLink} onClick={onClick} label={label} />
    </Flex>
  )
}

export const PhaseRightLinks = () => {
  const {
    right: { showLink, onClick, label }
  } = usePhaseLinks()

  return (
    <Flex
      height={`${TIMELINE_PHASE_ROW_HEIGHT_PX}px`}
      justifyContent="flex-end"
    >
      <RightLink showLink={showLink} onClick={onClick} label={label} />
    </Flex>
  )
}
